import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, Spinner, type IconName } from './Icon.js';
import { haptics } from '../services/telegram.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks interaction — visually distinct from disabled. */
  loading?: boolean;
  loadingLabel?: string;
  iconLeft?: IconName;
  iconRight?: IconName;
  fullWidth?: boolean;
  children: ReactNode;
}

/**
 * `loading` and `disabled` are deliberately different states with different
 * looks and different copy: "working on it" is not the same message as
 * "you can't do this" (docs/DESIGN-SYSTEM.md §8).
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingLabel,
  iconLeft,
  iconRight,
  fullWidth = false,
  className,
  disabled,
  onClick,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const blocked = disabled || loading;

  return (
    <button
      {...rest}
      type={type}
      className={clsx('btn', `btn--${variant}`, `btn--${size}`, fullWidth && 'btn--block', loading && 'is-loading', className)}
      disabled={blocked}
      aria-busy={loading || undefined}
      onClick={(event) => {
        if (blocked) return;
        haptics.impact('light');
        onClick?.(event);
      }}
    >
      {loading ? (
        <>
          <Spinner size={size === 'lg' ? 20 : 16} />
          <span className="btn__label">{loadingLabel ?? children}</span>
        </>
      ) : (
        <>
          {iconLeft ? <Icon name={iconLeft} size={size === 'lg' ? 20 : 18} /> : null}
          <span className="btn__label">{children}</span>
          {iconRight ? <Icon name={iconRight} size={size === 'lg' ? 20 : 18} /> : null}
        </>
      )}
    </button>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconName;
  label: string;
  size?: number;
  variant?: 'ghost' | 'surface';
}

export function IconButton({
  icon,
  label,
  size = 20,
  variant = 'ghost',
  className,
  onClick,
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      type="button"
      aria-label={label}
      className={clsx('icon-btn', `icon-btn--${variant}`, className)}
      onClick={(event) => {
        haptics.impact('light');
        onClick?.(event);
      }}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}
